/**
 * Regras de status do acompanhamento de pedidos PIX.
 *
 * Local ao módulo de propósito: a regra de quando cada ação aparece cresce a
 * cada fase, e duplicada em dois componentes ela diverge na primeira mudança.
 *
 * As chaves são derivadas no backend (PixAcompanhamentoTransformer) — aqui só
 * se decide o que fazer com elas.
 */

export const STATUS = {
  PAGO_INTEGRADO: 'pago_integrado',
  PAGO_NAO_INTEGRADO: 'pago_nao_integrado',
  FALHA_INTEGRACAO: 'falha_integracao',
  DEVOLVIDO: 'devolvido',
  DEVOLUCAO_NAO_CONFIRMADA: 'devolucao_nao_confirmada',
  AGUARDANDO: 'aguardando',
};

/**
 * Situações em que integrar o pedido no ERP faz sentido: o dinheiro está
 * confirmado e o pedido não chegou lá.
 *
 * `devolucao_nao_confirmada` entra porque é o pedido cujo estorno a adquirente
 * recusou — o dinheiro continua conosco e o pedido continua fora do ERP, então
 * integrar é justamente a saída. O backend já permite; era só o botão que não
 * aparecia.
 *
 * `devolvido` fica de fora: integrar um pedido cujo dinheiro voltou ao cliente
 * é o pior erro possível nesta tela.
 */
const STATUS_QUE_PERMITEM_INTEGRAR = [
  STATUS.PAGO_NAO_INTEGRADO,
  STATUS.FALHA_INTEGRACAO,
  STATUS.DEVOLUCAO_NAO_CONFIRMADA,
];

/**
 * Situações em que devolver o dinheiro ao cliente faz sentido: o pagamento está
 * confirmado e o pedido não chegou ao ERP.
 *
 * São as mesmas três de integrar, e isso é intencional — as duas ações
 * respondem ao mesmo estado ("dinheiro entrou, pedido não está no ERP") com
 * saídas opostas: empurrar ao ERP ou devolver.
 *
 * `devolucao_nao_confirmada` entra porque é o caso que motivou o módulo: a
 * adquirente recusou o estorno, o dinheiro continua conosco e o operador
 * precisa poder tentar de novo.
 *
 * `pago_integrado` fica de fora porque devolver o dinheiro de um pedido que já
 * virou pedido no ERP cria divergência fiscal, e `devolvido` porque não há o
 * que devolver. O backend recusa os dois de qualquer forma: o botão só espelha.
 */
const STATUS_QUE_PERMITEM_ESTORNAR = [
  STATUS.PAGO_NAO_INTEGRADO,
  STATUS.FALHA_INTEGRACAO,
  STATUS.DEVOLUCAO_NAO_CONFIRMADA,
];

/**
 * O status mais recente do pedido: o que o "Verificar situação" devolveu, quando
 * houve consulta, ou o que veio da lista. A consulta pode registrar o pagamento
 * com o modal aberto, e o modal inteiro precisa ler o mesmo status.
 */
export function statusAtual(pedido, situacao) {
  const badge = situacao && situacao.local && situacao.local.badge;

  return badge || (pedido && pedido.status) || null;
}

export function chaveDoStatus(pedido) {
  return (pedido && pedido.status && pedido.status.chave) || null;
}

export function podeIntegrar(pedido) {
  return STATUS_QUE_PERMITEM_INTEGRAR.includes(chaveDoStatus(pedido));
}

export function podeEstornar(pedido) {
  return STATUS_QUE_PERMITEM_ESTORNAR.includes(chaveDoStatus(pedido));
}
